import type { AppStore } from "@/store";
import { clientEnvModule } from "@/modules/client-env";
import { localStorageModule } from "@/modules/local-storage";
import type { OnboardingContext } from "./onboarding-types";

/**
 * Builds a snapshot of all the state sources the onboarding step predicates need.
 *
 * This is the single point of contact between the new OnboardingStore and the
 * legacy state scattered across PlatformOnboardingInfoObservable,
 * PerDeviceOnboardingInfoObservable, and environment modules.
 */
export function buildOnboardingContext(store: AppStore): OnboardingContext {
  const { onboardingInfo, perDeviceOnboardingInfo } = store.platformInfo;
  const { calendarOnboarding, electron } = store;

  /** We only support some features (like voice and meets) on Intel-based MacOS. */
  /** We only require onboarding for them on desktop **/
  const { isMacOs, isDesktop } = clientEnvModule;

  const isAppleSiliconMacDesktop = isDesktop() && isMacOs() && !electron.isIntelMacDesktop;
  const shouldShowNativeAppDownload = !isDesktop() && !localStorageModule.readIosCalendarSetup();

  return {
    // Server-driven
    shouldShowIcpRfi: onboardingInfo.shouldShowIcpRfi,

    // Device-local
    shouldShowCalendarOnboardingSetup: calendarOnboarding.shouldShow,
    shouldShowMeetingOnboardingSystemPermissions:
      perDeviceOnboardingInfo.shouldShowMeetingOnboardingSystemPermissions,
    shouldShowVoiceOnboardingSystemPermissions:
      perDeviceOnboardingInfo.shouldShowVoiceOnboardingSystemPermissions,
    hasCompletedOrSkippedNativeAppDownload:
      perDeviceOnboardingInfo.hasCompletedOrSkippedNativeAppDownload,
    // Environment / platform gates
    shouldShowNativeAppDownload,
    isAppleSiliconMacDesktop,

    // Calendar OAuth
    hasPendingCalendarConnection: calendarOnboarding.pendingCalendarConnection !== null,

    // ICP RFI substep routing
    rfiCompletedSteps: onboardingInfo.rfiCompletedSteps,
  };
}
